import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import { BellRing, ShieldCheck, CheckCircle2 } from "lucide-react";
import { apiClient } from "../lib/api";
import usePushNotifications from "../hooks/usePushNotifications";

/** Public one-tap opt-in page emailed to a CPD Preferred Provider's designated
 * driver — SMS is suspended CPD-wide, so this is their real-time pickup
 * beacon channel (in addition to the voice bridge + email that always fire). */
export default function CPDDriverOptin() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null); // "not_found" | "server_error" | null
  const push = usePushNotifications({ cpdDriverToken: token });

  const load = () => {
    setError(null);
    apiClient.get(`/cpd/driver-optin/${token}`)
      .then(({ data }) => setData(data))
      .catch((err) => setError(err?.response?.status === 404 ? "not_found" : "server_error"));
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const enablePush = async () => {
    const ok = await push.enable();
    if (ok) toast.success("Trip alerts enabled");
    else if (push.error) toast.error(push.error);
  };

  if (error === "not_found") {
    return <div className="min-h-screen flex items-center justify-center text-slate-500" data-testid="cpd-driver-optin-error">This link is invalid or has expired.</div>;
  }
  if (error === "server_error") {
    return (
      <div className="min-h-screen flex items-center justify-center text-center px-4" data-testid="cpd-driver-optin-server-error">
        <div>
          <div className="text-lg font-bold text-slate-800">We're having trouble loading this page right now.</div>
          <p className="text-sm text-slate-500 mt-1 mb-4">This isn't a bad link — just a temporary hiccup. Please try again.</p>
          <button onClick={load} data-testid="cpd-driver-optin-retry-btn" className="px-4 py-2 rounded-md bg-forest text-white text-sm font-bold">
            Retry
          </button>
        </div>
      </div>
    );
  }
  if (!data) {
    return <div className="min-h-screen flex items-center justify-center text-slate-400">Loading…</div>;
  }

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col items-center py-10 px-4">
      <div className="max-w-md w-full space-y-5" data-testid="cpd-driver-optin-page">
        <div className="flex items-center gap-2 text-forest font-bold text-sm uppercase tracking-widest">
          <ShieldCheck size={18} /> Medex CPD Transport
        </div>
        <div className="bg-white rounded-2xl border border-slate-200 p-6 space-y-3 text-center">
          <div className="text-lg font-bold text-slate_navy">Hi {data.driver_name || "there"}</div>
          <p className="text-sm text-slate-600">
            You're the designated pickup driver for <b>{data.provider_name}</b>. Turn on trip alerts so you
            get notified the instant a CPD pickup is assigned to you — you'll also always get a call and an
            email with the pickup/drop-off details either way.
          </p>
          {push.permission === "granted" ? (
            <div className="flex items-center justify-center gap-2 text-green-700 font-semibold text-sm py-3" data-testid="cpd-driver-optin-enabled">
              <CheckCircle2 size={16} /> Trip alerts enabled
            </div>
          ) : (
            <button
              onClick={enablePush}
              disabled={push.enabling || !push.supported}
              data-testid="cpd-driver-optin-enable-btn"
              className="w-full flex items-center justify-center gap-2 bg-forest text-white rounded-xl py-3 text-sm font-semibold disabled:opacity-50"
            >
              <BellRing size={16} /> {push.enabling ? "Enabling…" : "Turn on trip alerts"}
            </button>
          )}
          {!push.supported && (
            <div className="text-xs text-slate-400">Push isn't supported on this browser — you'll still get a call and email for every pickup.</div>
          )}
        </div>
      </div>
    </div>
  );
}
